import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

/**
 * Phase 193 — Super Admin deletion alerts (written only when a Municipal Admin deletes properties).
 *   useSuperDeletionAlertCount()  → unread count for the "Deleted Properties" sidebar badge (polled every minute,
 *                                   refreshed at once after a mark-as-read via `notifyDeletionAlertsChanged()`).
 *   useDeletionAlerts(state)      → the alert list for the Unread alerts strip (+ markRead / markAllRead helpers).
 */
export const DELETION_ALERTS_EVENT = "deletion-alerts:changed";
export const notifyDeletionAlertsChanged = () => { try { window.dispatchEvent(new Event(DELETION_ALERTS_EVENT)); } catch (e) { /* ignore */ } };

const REFRESH_MS = 60_000;

export function useSuperDeletionAlertCount(enabled = true) {
  const uid = useAuth()?.auth?.user?.id || null;
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) { setCount(0); return undefined; }
    let alive = true;
    const load = () => api.get("/platform/deletion-alerts/summary").then((r) => { if (alive) setCount(r.data?.unread || 0); }).catch(() => { /* badge is best-effort */ });
    load();
    const t = setInterval(load, REFRESH_MS);
    window.addEventListener(DELETION_ALERTS_EVENT, load);
    return () => { alive = false; clearInterval(t); window.removeEventListener(DELETION_ALERTS_EVENT, load); };
  }, [uid, enabled]);
  return count;
}

export function useDeletionAlerts({ state = "unread", limit = 20 } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    return api.get("/platform/deletion-alerts", { params: { state, limit } })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [state, limit]);
  useEffect(() => {
    load();
    window.addEventListener(DELETION_ALERTS_EVENT, load);
    return () => window.removeEventListener(DELETION_ALERTS_EVENT, load);
  }, [load]);
  const markRead = useCallback(async (id) => {
    await api.post(`/platform/deletion-alerts/${id}/read`);
    notifyDeletionAlertsChanged();
  }, []);
  const markAllRead = useCallback(async (code) => {
    const r = await api.post("/platform/deletion-alerts/read-all", null, { params: code ? { code } : {} });
    notifyDeletionAlertsChanged();
    return r.data;
  }, []);
  return { data, items: data?.items || [], unread: data?.unread || 0, total: data?.total || 0, loading, error, reload: load, markRead, markAllRead };
}
